import {
    el, setAttr, mount, setChildren, setStyle
} from 'redom';
import isEqual from 'lodash/isEqual';

import { ContentsManager } from '../Content/contentsManager';
import {
    ContentModel, IVariantModel
} from '../Content/contentModel';
import { ConfigurationTranslation } from './configurationTranslation';
import { ITextGeometry, IVariantText } from '../Content/Pattern/Decal/patternText';
import { IMaskGeometry, IVariantMask } from '../Content/Pattern/Decal/patternMask';
import { IVariantMeshesMaterials, IVariantMeshesProperties } from '../Content/contentModelVariantMesh';
import { SystemCamera } from '../System/systemCamera';

import './variantMeshDior.sass';
import { CROSS_ICON } from '../Ui/icons';

const setElementSelected = (button: HTMLElement): void => {
    const parent = button.parentNode;
    const children = parent.childNodes;
    for (let i = 0; i < children.length; i++) {
        const child = children[i];
        setAttr(child, { selected: false });
    }
    setAttr(button, { selected: true });
};

const insertAfter = (newNode: HTMLElement, existingNode: HTMLElement) => {
    existingNode.parentNode.insertBefore(newNode, existingNode.nextSibling);
};

export class ConfigurationVariantMeshDior {
    private system: SystemCamera;

    private container: HTMLElement;

    private configurationTranslation: ConfigurationTranslation

    private contentsManager: ContentsManager

    // CONFIG LAYER
    private configurationContainer: HTMLElement

    private variantContainer: HTMLElement

    private materialTitle: HTMLElement

    private menuList: HTMLElement

    // SUMMARY LAYER
    private summaryContainer: HTMLElement

    private title: HTMLElement

    private subTitle: HTMLElement

    private price: HTMLElement

    public confirm: HTMLElement

    // OTHER
    public exit: HTMLElement

    constructor(
        configurationTranslation: ConfigurationTranslation,
        container: HTMLElement,
        system: SystemCamera,
        contentsManager: ContentsManager
    ) {
        this.container = container;
        this.system = system;
        this.configurationTranslation = configurationTranslation;
        this.contentsManager = contentsManager;
        this.configurationTranslation.onChange(() => {
            this.updateTranslations();
        });

        setAttr(container, { 'data-naker-responsive': true, 'data-naker-configurator-collapsed': false });

        this.buildLeftLayer();
        this.addMeshPick(system);
    }

    private buildLeftLayer() {
        this.configurationContainer = el('div.naker-configurator-container', [
            this.summaryContainer = el('div.naker-summary-container', [
                this.title = el('div.naker-title',
                    this.configurationTranslation.getTranslatedString('title')),
                // this.subTitle = el('div.naker-subtitle',
                //     this.configurationTranslation.getTranslatedString('subtitle')),
                this.price = el('div.naker-price'),
                // this.confirm = el('div.naker-confirm', this.configurationTranslation.getTranslatedString('confirm')),
            ]),
            el('div.naker-menu', this.menuList = el('div.naker-menu-list')),
        ]);
        this.variantContainer = el('div.naker-variation',
            this.materialTitle = el('div.naker-title.naker-material-title'));

        mount(this.container, this.configurationContainer);

        this.exit = el('div.naker-button.naker-exit-button');
        this.exit.innerHTML = CROSS_ICON;
        mount(this.container, this.exit);

        this.hide();
    }

    private addMeshPick(system: SystemCamera) {
        // In order to make sure this is a click and not a drag&drop action
        let clickAction = false;
        system.canvas.addEventListener('pointerdown', (evt) => {
            clickAction = true;
            setTimeout(() => {
                clickAction = false;
            }, 200);
        });
        system.canvas.addEventListener('pointerup', (evt) => {
            if (clickAction) {
                const mesh = system.pickMesh(evt);
                if (mesh) {
                    this.showMeshVariantByMeshes([mesh.name]);
                }
            }
        });
    }

    private variantButtons: HTMLElement[] = []

    private variantPins: HTMLElement[] = []

    private buildMeshList() {
        const model = this.getModel();
        const variantMeshesMaterialsList = model.getVariantMeshesMaterialsList();
        const variantButtons = variantMeshesMaterialsList.map((v) => {
            const { nakerName } = v;
            const key = nakerName || v.meshes.join('');
            const name = this.configurationTranslation.getTranslatedString(key);
            const selected = model.getSelectedVariantMeshes(v.meshes);
            const color = (selected) ? this.system.toolMaterial.getColorFromMaterialKey(selected.material) : '';
            let pin;
            const button = el('div.naker-menu-mesh', {
                onclick: (e) => {
                    this.showMeshVariantByMeshes(v.meshes);
                }
            }, [
                el('div.naker-combo',
                    pin = el('div.naker-pin', { style: { background: color } })),
                el('div.naker-label', this.configurationTranslation.getTranslatedString(name)),
            ]);
            this.variantPins.push(pin);
            return button;
        });
        this.variantButtons = variantButtons;
        setChildren(this.menuList, variantButtons);
    }

    private getModel(): ContentModel {
        return this.contentsManager.getFirstModel();
    }

    private variantMeshesMaterialsList: IVariantMeshesProperties[]

    public showVariantMeshesMaterialsList(): void {
        setChildren(this.variantContainer, []);
        const model = this.getModel();
        this.variantMeshesMaterialsList = model.getVariantMeshesMaterialsList();
    }

    public showFirstVariantMesh(): void {
        this.currentNumber = null;
        if (this.variantMeshesMaterialsList.length) this.showMeshVariantByNumber(0, false);
    }

    private currentNumber: number

    private showMeshVariantByNumber(num: number, animate?: boolean) {
        let numMinMax = Math.max(num, 0);
        numMinMax = Math.min(numMinMax, this.variantMeshesMaterialsList.length - 1);
        if (numMinMax === this.currentNumber) return;
        this.currentNumber = numMinMax;
        const variantMeshesMaterials = this.variantMeshesMaterialsList[this.currentNumber];
        const parent = this.variantButtons[this.currentNumber];
        if (variantMeshesMaterials) this.showMeshVariant(variantMeshesMaterials, parent, animate);
    }

    private showMeshVariantByMeshes(meshes: string[]) {
        for (let i = 0; i < this.variantMeshesMaterialsList.length; i++) {
            if (isEqual(this.variantMeshesMaterialsList[i].meshes, meshes)) {
                this.showMeshVariantByNumber(i);
                break;
            }
        }
    }

    private showMeshVariant(
        variantMeshesMaterials: IVariantMeshesMaterials,
        parent: HTMLElement,
        animate = true
    ) {
        // Put back default mode in case we were on text input
        const { meshes } = variantMeshesMaterials;
        const model = this.getModel();
        model.flashMeshes(meshes);
        if (animate) this.chekView(variantMeshesMaterials);
        const materialListHtml = el('div.naker-combo-list',
            variantMeshesMaterials.materials.map((m) => {
                const rgb = this.system.toolMaterial.getColorFromMaterialKey(m);
                const materialHtml = el('div.naker-combo', {
                    onclick: (e) => {
                        setElementSelected(e.target.parentNode);
                        this.manageMaterialChange(variantMeshesMaterials, m);
                    }
                }, el('div.naker-pin', { style: { 'background-image': rgb } }));
                const isSelected = model.getSelectedVariantMeshes(meshes);
                if (isSelected) {
                    const isMaterialSelected = isEqual(isSelected.material, m);
                    if (isMaterialSelected) setAttr(materialHtml, { selected: true });
                }
                return materialHtml;
            }));
        setChildren(this.variantContainer, [materialListHtml]);
        const { decalGeometry } = variantMeshesMaterials;
        if (decalGeometry) {
            if (decalGeometry.type === 'text') {
                const html = this.addTextInput(variantMeshesMaterials);
                setChildren(this.variantContainer, [html, materialListHtml]);
            } else if (decalGeometry.type === 'mask') {
                const html = this.addMaskInput(variantMeshesMaterials.decalGeometry);
                setChildren(this.variantContainer, [html]);
            }
        }
        insertAfter(this.variantContainer, parent);
        this.checkMaterialName(variantMeshesMaterials);
    }

    private manageMaterialChange(
        variantMeshesMaterials: IVariantMeshesProperties,
        material: string
    ) {
        const model = this.getModel();
        model.setVariantMeshes({ meshes: variantMeshesMaterials.meshes, material }, true);
        this.checkMaterialName(variantMeshesMaterials);
        const color = this.system.toolMaterial.getColorFromMaterialKey(material);
        setStyle(this.variantPins[this.currentNumber], { background: color });
        this.chekView(variantMeshesMaterials);
        this.notifyChange();
    }

    private notifyChange() {
        if (this.onChangeFunction) {
            const variant = this.getCurrentVariant();
            this.onChangeFunction(variant);
        }
    }

    public getCurrentVariant(): IVariantModel {
        const model = this.getModel();
        const variantMeshesList = model.getVariantMeshesList();
        const variantTextList = model.getVariantTextList();
        const variantMaskList = model.getVariantMaskList();
        return {
            name: '', variantMeshesList, variantTextList, variantMaskList
        };
    }

    private onChangeFunction: (variant: IVariantModel) => void

    public onChange(funct: (variant: IVariantModel) => void): void {
        this.onChangeFunction = funct;
    }

    private chekView(variantMeshes: IVariantMeshesProperties) {
        const system = this.contentsManager._system;
        const view = variantMeshes.cameraView;
        if (view) system.focusOnArcCameraAngles(view);
    }

    public addTextInput(variantMeshes: IVariantMeshesProperties): HTMLElement[] {
        const model = this.getModel();
        const patternText = model.getPatternText(variantMeshes.meshes);
        if (!patternText) return [];
        const variantText = patternText.getVariant() as IVariantText;
        const { text } = variantText;
        const value = (text) || '';
        const textInput = el('input.naker-input', {
            placeholder: this.configurationTranslation.getTranslatedString('placeholder'),
            value,
            oninput: () => {
                const text = patternText.setText(textInput.value);
                setAttr(textInput, { value: text });
                this.notifyChange();
            }
        }) as HTMLInputElement;
        // Manage "Go" button on Androïd and hide the keyboard
        textInput.addEventListener('keydown', (e) => {
            const { key } = e;
            if (key === 'Enter') {
                textInput.setAttribute('readonly', 'true');
                setTimeout(() => {
                    textInput.blur();
                    textInput.removeAttribute('readonly');
                    textInput.removeAttribute('disabled');
                }, 100);
            }
            this.chekView(variantMeshes);
        });
        textInput.focus();
        const textElements = [textInput];

        const textGeometry = variantMeshes.decalGeometry as ITextGeometry;
        if (textGeometry.fonts && textGeometry.fonts.length > 1) {
            const fontContainer = el('div.naker-font-list.naker-button-text-list',
                textGeometry.fonts.map((c) => el('div.naker-button-text', {
                    style: { 'font-family': c },
                    onclick: (e) => {
                        patternText.setFont(c);
                        this.notifyChange();
                    }
                }, 'ABC')));
            textElements.push(fontContainer);
        }
        return textElements;
    }

    private addMaskInput(variantMeshes: IVariantMeshesProperties): HTMLElement {
        const model = this.getModel();
        const maskGeometry = variantMeshes.decalGeometry as IMaskGeometry;
        const patternMask = model.getPatternMask(variantMeshes.meshes);
        if (!patternMask) return null;
        const maskVariant = patternMask.getVariant() as IVariantMask;
        const { mask } = maskVariant;
        const maskListHtml = el('div.naker-combo-list',
            maskGeometry.maskList.map((m) => {
                const materialHtml = el('div.naker-combo', {
                    onclick: (e) => {
                        setElementSelected(e.target.parentNode);
                        patternMask.setMask(m);
                        this.notifyChange();
                    }
                }, el('div.naker-pin', { style: { 'background-image': `url(${m})` } }));
                if (m === mask) setAttr(materialHtml, { selected: true });
                return materialHtml;
            }));
        return maskListHtml;
    }

    private checkMaterialName(variantMeshesMaterials: IVariantMeshesMaterials) {
        const model = this.getModel();
        const { meshes } = variantMeshesMaterials;
        const { decalGeometry } = variantMeshesMaterials;

        let selected = model.getSelectedVariantMeshes(meshes);
        if (!selected && decalGeometry) {
            selected = model.getSelectedVariantMeshes([decalGeometry.sourceMeshName]);
        }
        if (selected && selected.material) {
            const material = this.system.toolMaterial.getMaterialByKey(selected.material);
            const name = material.name || material.colorName;
            const tsString = this.configurationTranslation.getTranslatedString(name);
            this.materialTitle.textContent = tsString;
        } else {
            this.materialTitle.textContent = '';
        }
    }

    public updateTranslations(): void {
        this.title.textContent = this.configurationTranslation.getTranslatedString('title');
        // this.subTitle.textContent = this.configurationTranslation.getTranslatedString('subtitle');
        // this.confirm.textContent = this.configurationTranslation.getTranslatedString('confirm');
    }

    public setPrice(price: string): void {
        this.price.innerHTML = price;
    }

    public show(): void {
        setStyle(this.configurationContainer, { opacity: 1 });
        this.buildMeshList();
        this.showFirstVariantMesh();
    }

    public hide(): void {
        setStyle(this.configurationContainer, { opacity: 0 });
    }
}
